// Service API général - gère les tokens JWT et les requêtes HTTP
// Hôte de l'API : api.mbiyo.org (Cloudflare — pare-feu, Turnstile, contournement du blocage FAI).
// Sécurité 2026-11-02 : une valeur d'environnement qui pointerait encore vers l'hôte Railway direct est ignorée.
const RAW_API_URL = process.env.REACT_APP_API_URL || '';
const API_BASE_URL = RAW_API_URL && !/up\.railway\.app/i.test(RAW_API_URL)
  ? RAW_API_URL
  : (
  window.location.hostname === 'localhost'
    ? 'http://localhost:5000/api'
    : 'https://api.mbiyo.org/api'
);

class ApiService {
  constructor() {
    this.baseURL = API_BASE_URL;
  }

  // Récupérer le token depuis localStorage
  getToken(userType = 'user') {
    const key = userType === 'admin' ? 'adminToken' : userType === 'driver' ? 'driverToken' : 'userToken';
    return localStorage.getItem(key);
  }

  // Stocker le token
  setToken(token, userType = 'user') {
    const key = userType === 'admin' ? 'adminToken' : userType === 'driver' ? 'driverToken' : 'userToken';
    if (token) {
      localStorage.setItem(key, token);
    }
  }

  // Supprimer le token
  removeToken(userType = 'user') {
    const key = userType === 'admin' ? 'adminToken' : userType === 'driver' ? 'driverToken' : 'userToken';
    localStorage.removeItem(key);
  }

  // Requête HTTP générique avec timeout
  async request(endpoint, options = {}) {
    const userType = options.userType || 'user';
    const token = this.getToken(userType);
    const timeout = options.timeout || 30000; // 30 secondes par défaut pour éviter les timeouts

    const headers = {
      'Content-Type': 'application/json',
      'X-Mbiyo-App': 'web', // le serveur sait quelle app est en maintenance
      ...options.headers
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    try {
      console.log(`📡 ${options.method || 'GET'} ${this.baseURL}${endpoint}` );
      
      // Ajouter un timeout à la requête
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeout);
      
      const response = await fetch(`${this.baseURL}${endpoint}`, {
        ...options,
        headers,
        signal: controller.signal
      });

      clearTimeout(timeoutId);
      console.log(`📊 Response Status: ${response.status}`);

      let data;
      let text = '';
      try {
        text = await response.text();
        data = text ? JSON.parse(text) : { error: 'Réponse vide' };
      } catch (parseError) {
        console.error('❌ Erreur parsing JSON:', parseError, 'Text:', text);
        data = { error: 'Erreur lors du parsing de la réponse' };
      }

      if (!response.ok) {
        console.error('❌ Réponse non-OK:', { status: response.status, data });
        // Mot de passe temporaire à changer (sécurité 2026-11-02) : la fenêtre PasswordGate s'ouvre
        if (response.status === 403 && data?.code === 'password_change_required') {
          try { window.dispatchEvent(new CustomEvent('mbiyo:password-change-required')); } catch (_) {}
        }
        // Auto-logout si token invalide (401) SAUF routes d'auth et appels de fond.
        // GARDE-FOUS anti-boucle de rechargement :
        //  • silent401 : les pollers (notifications, stats…) ne déconnectent JAMAIS
        //    — un endpoint legacy en 401 ne doit pas recharger la page à chaque tick ;
        //  • sans token stocké : rien à déconnecter → pas de reload ;
        //  • au plus UN reload par chargement de page (drapeau module).
        if (response.status === 401 && !endpoint.startsWith('/auth/') && !options.silent401) {
          const hadToken = localStorage.getItem('userToken') || localStorage.getItem('driverToken');
          // Connexion par téléphone (2026-10-30) : jeton d'appareil (180 j) → nouveau jeton d'accès, puis
          // la requête repart une fois ; sans jeton d'appareil valable → déconnexion comme avant.
          if (hadToken && !options._retried && data?.code !== 'session_revoked' && data?.code !== 'account_blocked') {
            const ok = await this.silentRefresh(userType);
            if (ok) return this.request(endpoint, { ...options, _retried: true });
          }
          if (hadToken && !ApiService._didAuthReload) {
            ApiService._didAuthReload = true;
            localStorage.removeItem('userToken');
            localStorage.removeItem('driverToken');
            localStorage.removeItem('currentUser');
            localStorage.removeItem('currentDriver');
            window.location.reload();
            return;
          }
        }
        // Maintenance : afficher l'écran tout de suite (sans attendre le prochain contrôle)
        if (response.status === 503 && data?.code === 'maintenance') {
          window.dispatchEvent(new CustomEvent('mbiyo:maintenance'));
        }
        const errorMessage = data.error || `Erreur HTTP ${response.status}`;
        const error = new Error(errorMessage);
        error.status = response.status;
        error.error = errorMessage;
        error.code = data?.code || null; // code métier (ex. V7 : phone_taken, cooldown, invalid_code)
        error.data = data;
        throw error;
      }

      if (process.env.NODE_ENV !== 'production') console.log('✅ Réponse OK:', endpoint); // jamais le corps (jetons, profils) — 2026-11-01
      return data;
    } catch (error) {
      console.error('❌ Erreur API:', error);
      
      // Mieux gérer les différents types d'erreurs
      if (error.name === 'AbortError') {
        throw new Error('Timeout - La requête a dépassé ' + Math.floor(timeout/1000) + ' secondes');
      }
      
      throw error;
    }
  }

  // ── Connexion par téléphone (2026-10-30) : session de 180 jours par navigateur ──
  // Le jeton d'appareil est gardé dans localStorage ('userRefresh') ; il ne sert qu'à obtenir un
  // nouveau jeton d'accès (7 j) auprès de /auth/refresh. Chauffeur : le jeton d'accès utilisateur
  // est ensuite échangé contre une session chauffeur (/auth/driver-session).
  getRefreshToken() { return localStorage.getItem('userRefresh'); }
  setRefreshToken(token) { if (token) localStorage.setItem('userRefresh', token); }
  removeRefreshToken() { localStorage.removeItem('userRefresh'); }

  /** Identité de ce navigateur pour le jeton d'appareil (nom court, jamais l'UA complet). */
  deviceInfo() {
    const ua = navigator.userAgent || '';
    const os = /iPhone|iPad/.test(ua) ? 'iOS' : /Android/.test(ua) ? 'Android' : /Windows/.test(ua) ? 'Windows' : /Mac OS/.test(ua) ? 'macOS' : /Linux/.test(ua) ? 'Linux' : 'Web';
    const browser = /Edg\//.test(ua) ? 'Edge' : /OPR\//.test(ua) ? 'Opera' : /Chrome\//.test(ua) ? 'Chrome' : /Safari\//.test(ua) ? 'Safari' : /Firefox\//.test(ua) ? 'Firefox' : 'Navigateur';
    return { app: 'web', name: `${browser} · ${os}`, platform: 'web' };
  }

  /** Jeton d'appareil → nouveau jeton d'accès utilisateur (+ session chauffeur si userType = 'driver'). */
  async silentRefresh(userType = 'user') {
    if (ApiService._refreshing) return ApiService._refreshing;
    ApiService._refreshing = (async () => {
      const rt = this.getRefreshToken();
      if (!rt) return false;
      try {
        const res = await fetch(`${this.baseURL}/auth/refresh`, {
          method: 'POST', headers: { 'Content-Type': 'application/json', 'X-Mbiyo-App': 'web' },
          body: JSON.stringify({ refresh_token: rt }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok || !data?.token) {
          if (res.status === 401) this.removeRefreshToken(); // appareil déconnecté / compte bloqué
          return false;
        }
        this.setToken(data.token, 'user');
        if (data.user) localStorage.setItem('currentUser', JSON.stringify(data.user));
        if (userType === 'driver') {
          const r2 = await fetch(`${this.baseURL}/auth/driver-session`, {
            method: 'POST', headers: { 'Content-Type': 'application/json', 'X-Mbiyo-App': 'web', Authorization: `Bearer ${data.token}` }, body: '{}',
          });
          const d2 = await r2.json().catch(() => ({}));
          if (!r2.ok || !d2?.token) return false;
          this.setToken(d2.token, 'driver');
          if (d2.user) localStorage.setItem('currentDriver', JSON.stringify({ ...d2.user, status: d2.status || d2.user.status }));
        }
        return true;
      } catch (_) {
        return false;
      }
    })();
    try { return await ApiService._refreshing; } finally { ApiService._refreshing = null; }
  }

  // GET
  get(endpoint, options = {}) {
    return this.request(endpoint, { ...options, method: 'GET' });
  }

  // POST
  post(endpoint, body, options = {}) {
    return this.request(endpoint, {
      ...options,
      method: 'POST',
      body: JSON.stringify(body)
    });
  }

  // PUT
  put(endpoint, body, options = {}) {
    return this.request(endpoint, {
      ...options,
      method: 'PUT',
      body: JSON.stringify(body)
    });
  }

  // PATCH
  patch(endpoint, body, options = {}) {
    return this.request(endpoint, {
      ...options,
      method: 'PATCH',
      body: JSON.stringify(body)
    });
  }

  // DELETE
  delete(endpoint, options = {}) {
    return this.request(endpoint, { ...options, method: 'DELETE' });
  }
}

const apiService = new ApiService();
export default apiService;
